import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  CSSProperties
} from 'react';
import {
  useSprings,
  animated,
  to as interpolate,
  config,
} from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import './Deck.scss';
import { CardLearningStatus, WordData } from '@shared/types/words.ts';
import { Card } from '@app/components/Card.tsx';
import FormCheck from 'react-bootstrap/FormCheck';
import CloseButton from 'react-bootstrap/CloseButton';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { wordsLoadingAtom, wordsLoadMoreAtom } from '@app/atoms/wordsAtoms';
import { useAtomValue, useSetAtom } from 'jotai';

interface CardsSpringOpts {
  x?: number;
  y?: number;
  scale?: number;
  rot?: number;
  opacity?: number;
  delay?: number;
  [key: string]: any;
}

type SpringFromFn = (i: number) => CardsSpringOpts

type SpringToFn = (i: number, l: number, curIdx: number) => CardsSpringOpts

type AnimateCardFn = (a: { index: number, active: boolean, mx: number, xDir: number }) => void;
type DeckMode = 'main' | 'unstudied' | 'remembered';

// interpolates rotation and scale into a css transform
const setTransforms = (r: number, s: number) => (`perspective(1500px) rotateZ(${r}deg) scale(${s})`);

// helper function to map values
const mapToRange = (number: number, in_min: number, in_max: number, out_min: number, out_max: number) => {
  return (((number - in_min) * (out_max - out_min)) / (in_max - in_min) + out_min);
}
const calcOpacityChange = (x: number, i: number, containerWidth: number, cardStackLength: number) => {
  const startingValues = (i + 1) / cardStackLength;
  if (!isNaN(+x)) {
    x = +x;
  } else {
    // if card not moving
    return startingValues;
  }
  // distance from card stack center
  const distanceFromCenter = Math.abs((x / 100) * (i + 1));
  // when card is outside of container
  const inMax = containerWidth / 2;
  return (
    startingValues +
    parseFloat(mapToRange(distanceFromCenter, 0, inMax, 0, 1).toFixed(2)) +
    0.1
  );
};

const filterCardsByMode = (
  sourceCards: WordData[],
  mode: DeckMode,
  cardStatuses?: Record<string, CardLearningStatus | null>
): WordData[] => {
  if (mode === 'main') {
    return sourceCards;
  }

  const targetStatus: CardLearningStatus = mode === 'unstudied' ? 'not_remembered' : 'remembered';
  return sourceCards.filter((card) => {
    const status = cardStatuses?.[card.id] ?? card.learningStatus;
    return status === targetStatus;
  });
};

interface CardsState {
  currentCard: WordData | null;
  currentIndex: number;
  currentId: string;
  stack: WordData[];
  cutFactor: number;
  cutLength: number;
}

// set config for different spring animations
const springConfigs = {
  cardActive: config.stiff,
  cardFlyOut: config.molasses,
  cardDefault: config.slow,
  cardReset: config.gentle,
};

// DECK COMPONENT
interface DeckProps {
  cards: WordData[];
  loadMoreEnabled: boolean;
  onEmpty?: () => void;
  onChange?: (a: { cardStack: WordData[], currentCard: WordData}) => void;
  onLike?: (card: WordData) => void;
  onDislike?: (card: WordData) => void;
  onSave?: (card: WordData) => void;
  onCardout?: (card: WordData) => void;
  onAnimation?: (a: { x: number, rotation: number, scale: number }) => void;
  onClose?: () => void;
  onReset?: () => void;
  initialCurrentCardIndex?: number;
  deckId?: string;
  mode?: DeckMode;
  onProgress?: (progress: { currentCardIndex: number }) => void;
  onCardStatusChange?: (card: WordData, status: CardLearningStatus | null) => void;
  initialCardStatuses?: Record<string, CardLearningStatus | null>;
  transformStartValues?: CardsSpringOpts;
  transformEndValues?: CardsSpringOpts;
  yOffset?: number;
  maxVelocity?: number;
  debounceTime?: number;
  rotationFactor?: number;
  fadeEffect?: boolean;
  cardSizeOnHover?: number;
  rotationThreshold?: number;
  maxVisibleCards?: number;
}
function Deck({
  cards,
  loadMoreEnabled,
  onEmpty,
  onChange,
  onLike,
  onDislike,
  onSave,
  onCardout,
  onAnimation,
  onClose,
  onReset,
  initialCurrentCardIndex = 0,
  deckId,
  mode = 'main',
  initialCardStatuses,
  onProgress,
  onCardStatusChange,
  transformStartValues = { y: 0, scale: 1.5 },
  transformEndValues = {},
  yOffset = 6,
  maxVelocity = 0.2,
  debounceTime = 200,
  rotationFactor = 1,
  fadeEffect = true,
  cardSizeOnHover = 1.02,
  rotationThreshold = 50,
  maxVisibleCards = 5,
}: DeckProps) {
  // const deckRef = useRef<HTMLDivElement | null>(null);
  const onCardOutTimer = useRef<number>(0);
  const flippedOutCardsRef = useRef<Set<string>>(new Set()); // The set flags all the cards that are flicked out
  const skipedCardsRef = useRef<Set<string>>(new Set()); // The set flags all the cards that are skipped
  const rememberedCardsRef = useRef<Set<string>>(new Set()); // The set flags all the cards that are remembered
  const repeatSkipedCardsRef = useRef<Set<string>>(new Set());
  const repeatRememberedCardsRef = useRef<Set<string>>(new Set());
  const [visibleCards, setVisibleCards] = useState<CardsState>({
    currentCard: null,
    currentIndex: -1,
    currentId: '',
    stack: [],
    cutFactor: 0,
    cutLength: 0
  });
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [textMode, setTextMode] = useState<boolean>(false);
  const [activeCards, setActiveCards] = useState<WordData[]>(filterCardsByMode(cards, mode, initialCardStatuses));
  const [repeatMode, setRepeatMode] = useState<CardLearningStatus | null>(null);
  const deckIdentifierRef = useRef('');
  const appliedInitialIndexRef = useRef(0);

  const wordsLoading = useAtomValue(wordsLoadingAtom);
  const dispatchLoadMore = useSetAtom(wordsLoadMoreAtom);
  const isRepeatSession = mode !== 'main' || !!repeatMode;

  let cardTapPass: (() => void) | undefined;

  useLayoutEffect(() => {
    function handleResize() {
      setContainerWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // FIXME DELAY
  // These two are just helpers, they curate spring data, values that are later being interpolated into css
  const from: SpringFromFn = (_i) => {
    const i = _i - visibleCards.cutFactor;

    return {
      x: transformStartValues.x ? transformStartValues.x : 0,
      y: transformStartValues.y ? transformStartValues.y : 0,
      scale: transformStartValues.scale ? transformStartValues.scale : 1.08,
      rot: transformStartValues.rotation ? transformStartValues.rotation : 0,
      opacity: transformStartValues.opacity ? transformStartValues.opacity : 1,
      delay: transformStartValues.delay ? i * transformStartValues.delay : 0
    };
  };

  const to: SpringToFn = (_i, _cardStackLength, _currentCardIndex) => {
    const i = _i - visibleCards.cutFactor;
    const cardStackLength = Math.min(maxVisibleCards, _cardStackLength);
    const currentCardIndex = _currentCardIndex - visibleCards.cutFactor;

    return {
      x: transformEndValues.x ? transformEndValues.x : 0,
      y: ((i % cardStackLength) - currentCardIndex) * yOffset,
      scale: transformEndValues.scale ? transformEndValues.scale : 1,
      rot: transformEndValues.rotation ? transformEndValues.rotation : (_i < _cardStackLength - 1 ? -2 + Math.random() * 4 : 0),
      opacity: transformEndValues.opacity ? transformEndValues.opacity : 1,
      delay: transformEndValues.delay ? i * transformEndValues.delay : i * 10,
    };
  };

  // Create a bunch of springs using the helpers above
  const [animationProps, springsApi] = useSprings(visibleCards.stack.length, (i: number) => {
    return {
    ...to(i, visibleCards.stack.length, visibleCards.currentIndex),
    from: from(i),
    };
  }, [visibleCards]);

  // Trigger events
  const _onCardOut = (card: WordData, dirX: number) => {
    if (dirX > 0) {
      skipedCardsRef.current.delete(card.id);
      rememberedCardsRef.current.add(card.id);
      if (isRepeatSession) {
        repeatSkipedCardsRef.current.delete(card.id);
        repeatRememberedCardsRef.current.add(card.id);
      }
      if (typeof onLike === 'function') {
        onLike(card);
      }
      emitStatusChange(card, 'remembered');
    } else {
      rememberedCardsRef.current.delete(card.id);
      skipedCardsRef.current.add(card.id);
      if (isRepeatSession) {
        repeatRememberedCardsRef.current.delete(card.id);
        repeatSkipedCardsRef.current.add(card.id);
      }
      if (typeof onDislike === 'function') {
        onDislike(card);
      }
      emitStatusChange(card, 'not_remembered');
    }
    if (typeof onCardout === 'function') {
      onCardout(card);
    }
    emitProgress();
  };

  const emitProgress = () => {
    if (isRepeatSession) {
      return;
    }
    if (typeof onProgress === 'function') {
      onProgress({
        currentCardIndex: flippedOutCardsRef.current.size
      });
    }
  };

  const emitStatusChange = (card: WordData, status: CardLearningStatus | null) => {
    if (typeof onCardStatusChange === 'function') {
      onCardStatusChange(card, status);
    }
  };

  // Move Cards down everytime one gets flipped out
  useEffect(() => {
    if (!visibleCards.cutLength) {
      return;
    }
    springsApi.start((_i: number) => {
      const i = _i - visibleCards.cutFactor;
      return {
        to: to(_i, visibleCards.stack.length, visibleCards.currentIndex),
        config: i < visibleCards.cutLength ? springConfigs.cardReset : config.default
      };
    });
  }, [springsApi, visibleCards]);

  const updateVisibleCards = (sourceCards: WordData[] = activeCards): CardsState => {
    const stack = sourceCards.slice(flippedOutCardsRef.current.size, sourceCards.length).reverse();
    const currentIndex =  stack.length - 1;
    const cutFactor = Math.max(0, stack.length - maxVisibleCards);
    const currentCard = stack[stack.length - 1] || null;

    const newCardsState = {
      currentCard,
      currentIndex,
      currentId: currentCard?.id || '',
      stack,
      cutFactor,
      cutLength: stack.length - cutFactor
    };
    setVisibleCards(newCardsState);

    return newCardsState;
  };

  useEffect(() => {
    const newDeckId = deckId ?? '';
    const filteredCards = filterCardsByMode(cards, mode, initialCardStatuses);
    const initialIndex = mode === 'main' ? (initialCurrentCardIndex ?? 0) : 0;
    const targetIndex = Math.max(0, Math.min(initialIndex, filteredCards.length));
    const deckChanged = newDeckId !== deckIdentifierRef.current;
    const shouldFreezeNonMainDeck = mode !== 'main' && flippedOutCardsRef.current.size > 0;

    if ((repeatMode || shouldFreezeNonMainDeck) && !deckChanged) {
      updateVisibleCards(activeCards);
      return;
    }

    if (deckChanged) {
      deckIdentifierRef.current = newDeckId;
      flippedOutCardsRef.current.clear();
      rememberedCardsRef.current.clear();
      skipedCardsRef.current.clear();
      repeatRememberedCardsRef.current.clear();
      repeatSkipedCardsRef.current.clear();
      setRepeatMode(null);
      setActiveCards(filteredCards);
      appliedInitialIndexRef.current = 0;
      if (initialCardStatuses) {
        cards.forEach((card) => {
          const status = initialCardStatuses[card.id];
          if (status === 'remembered') {
            rememberedCardsRef.current.add(card.id);
          } else if (status === 'not_remembered') {
            skipedCardsRef.current.add(card.id);
          }
        });
      }
    }

    const currentApplied = appliedInitialIndexRef.current;
    const answeredCardsCount = mode === 'main'
      ? rememberedCardsRef.current.size + skipedCardsRef.current.size
      : 0;
    // Guard against stale `currentCardIndex` in persisted progress.
    // Resume should not skip further than cards that actually have status.
    const safeTargetIndex = mode === 'main'
      ? Math.min(targetIndex, answeredCardsCount)
      : targetIndex;
    const limitIndex = Math.min(Math.max(safeTargetIndex, answeredCardsCount), filteredCards.length);

    if (limitIndex > currentApplied) {
      for (let i = currentApplied; i < limitIndex; i++) {
        flippedOutCardsRef.current.add(filteredCards[i].id);
      }
      appliedInitialIndexRef.current = limitIndex;
    } else if (deckChanged && limitIndex < currentApplied) {
      // Clean-up when deck changes but fetched target is smaller than previous
      flippedOutCardsRef.current.clear();
      appliedInitialIndexRef.current = limitIndex;
      for (let i = 0; i < limitIndex; i++) {
        flippedOutCardsRef.current.add(filteredCards[i].id);
      }
    }

    updateVisibleCards(filteredCards);
  }, [cards, mode, deckId, initialCurrentCardIndex, initialCardStatuses, repeatMode, activeCards]);

  useEffect(() => {
    if (repeatMode) {
      return;
    }
    if (mode !== 'main' && flippedOutCardsRef.current.size > 0) {
      return;
    }
    const filteredCards = filterCardsByMode(cards, mode, initialCardStatuses);
    setActiveCards(filteredCards);
    updateVisibleCards(filteredCards);
  }, [cards, mode, initialCardStatuses, repeatMode]);

  useEffect(() => {
    if (!initialCardStatuses || !Object.keys(initialCardStatuses).length) {
      return;
    }
    cards.forEach((card) => {
      const status = initialCardStatuses[card.id];
      if (!status) {
        return;
      }
      rememberedCardsRef.current.delete(card.id);
      skipedCardsRef.current.delete(card.id);
      if (status === 'remembered') {
        rememberedCardsRef.current.add(card.id);
      } else if (status === 'not_remembered') {
        skipedCardsRef.current.add(card.id);
      }
    });
  }, [cards, initialCardStatuses]);

  // trigger onChange event when card stack changes
  useEffect(() => {
    if (typeof onChange === 'function') {
      onChange({
        currentCard: visibleCards.stack[visibleCards.currentIndex],
        cardStack: visibleCards.stack,
      });
    }
  }, [visibleCards]);

  const animateCard: AnimateCardFn = ({ index, active, mx, xDir }) => {
    let y = 0;
    let opacity = 0;
    springsApi.start((_i: number) => {
      const i = _i - visibleCards.cutFactor;
      const _card = visibleCards.stack[_i];

      // Move previous Cards down
      if (index !== _i && index !== -1) {
        y = Math.abs((mx / 100) * (i + 1)) + ((i % visibleCards.cutLength) - (index - visibleCards.cutFactor)) * yOffset;
        if (fadeEffect) {
          opacity = calcOpacityChange(mx, i, containerWidth, visibleCards.cutLength);
        }
        // If Card is active return translated y value
        if (active)
          return {
            y,
            opacity: fadeEffect ? opacity : 1,
          };
        // If Card is NOT active return normal position in stack
        return {
          y: ((i % visibleCards.cutLength) - (index - visibleCards.cutFactor)) * yOffset,
          opacity: 1,
        };
      }

      // Only change spring-data for the current spring

      const isFlippedOutCard = flippedOutCardsRef.current.has(_card.id);
      const x = isFlippedOutCard
        ? (containerWidth + 200) * xDir
        : active
        ? mx
        : 0; // When a card is a flippedOutCard it flys out left or right, otherwise goes back to zero
      const rot =
        x > rotationThreshold || x < -rotationThreshold
          ? (mx / 100) * rotationFactor + (isFlippedOutCard ? xDir * 10 : 0)
          : 0; // How much the card tilts
      const scale = active ? cardSizeOnHover : 1; // Active cards lifts up a bit

      if (isFlippedOutCard) {
        clearTimeout(onCardOutTimer.current);
        onCardOutTimer.current = window.setTimeout(() => {
          const { stack } = updateVisibleCards();

          _onCardOut(_card, xDir);
          // If Stack is empty
          if (!stack.length && typeof onEmpty === 'function') {
            onEmpty();
          }
        }, debounceTime);
      }

      if (index === -1) { // TODO: refactor this section
        clearTimeout(onCardOutTimer.current);
        onCardOutTimer.current = window.setTimeout(() => {
          updateVisibleCards();
        }, debounceTime);
      }

      if (typeof onAnimation == 'function')
        onAnimation({
          x,
          rotation: rot,
          scale,
        });

      // return spring data for current card
      return {
        x,
        rot,
        scale,
        opacity: 1,
        delay: 0,
        config: active
          ? springConfigs.cardActive
          : isFlippedOutCard
            ? springConfigs.cardFlyOut
            : springConfigs.cardDefault,
      };
    });
  };

  const handleActiveCardMount = (tapHandler: (() => void) | undefined) => {
    cardTapPass = tapHandler;
  }

  const handleTap = (_: number) => {
    if (typeof cardTapPass === 'function') {
      cardTapPass();
    }
  }

  // Create a gesture, we're interested in down-state, delta (current-pos - click-pos), direction and velocity
  const bindGesture = useDrag(
    ({
      args: [index],
      active,
      movement: [mx],
      direction: [xDir],
      velocity: [vx],
      tap,
    }) => {
      if (textMode) {
        return;
      }
      if (tap) {
        handleTap(index);
      }
      const trigger = vx > maxVelocity; // If you flick hard enough it should trigger the card to fly out

      // If finger up and trigger velocity is reached, we flag the card ready to fly out
      if (!active && trigger){
        flippedOutCardsRef.current.add(visibleCards.stack[index].id);
        animateCard({ index, active: true, mx, xDir });
      } else {
        animateCard({ index, active, mx, xDir });
      }
    }
  );

  const castCardAway = (remember: boolean) => {
    if (!visibleCards.currentCard || flippedOutCardsRef.current.has(visibleCards.currentCard.id)) {
      return;
    }
    flippedOutCardsRef.current.add(visibleCards.currentCard.id);
    const xDir = remember ? 1 : -1;
    animateCard({
      index: visibleCards.currentIndex,
      active: true,
      mx: (containerWidth + 200) * xDir,
      xDir
    });
  };

  const restorePreviousCard = () => {
    if (!visibleCards.currentCard || flippedOutCardsRef.current.has(visibleCards.currentCard.id)) {
      return;
    }
    const prevCard = Array.from(flippedOutCardsRef.current).pop() as string;
    flippedOutCardsRef.current.delete(prevCard);
    rememberedCardsRef.current.delete(prevCard);
    skipedCardsRef.current.delete(prevCard);
    repeatRememberedCardsRef.current.delete(prevCard);
    repeatSkipedCardsRef.current.delete(prevCard);
    const prevCardData = cards.find((card) => card.id === prevCard);
    if (prevCardData) {
      emitStatusChange(prevCardData, null);
    }

    emitProgress();
    animateCard({
      index: -1,
      active: false,
      mx: 0,
      xDir: 0
    })
  };

  const handleCardModeChange = (active: boolean) => {
    console.log('handleCardModeChange', active);
    setTextMode(active);
  };

  const handleCloseCards = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleRepeatRememberedCards = () => {
    const repeatedCards = cards.filter((card) => rememberedCardsRef.current.has(card.id));
    if (!repeatedCards.length) {
      return;
    }

    setRepeatMode('remembered');
    setActiveCards(repeatedCards);
    repeatRememberedCardsRef.current.clear();
    repeatSkipedCardsRef.current.clear();
    flippedOutCardsRef.current.clear();
    appliedInitialIndexRef.current = 0;
    updateVisibleCards(repeatedCards);
  };

  const handleRepeatUnstudiedCards = () => {
    const repeatedCards = cards.filter((card) => skipedCardsRef.current.has(card.id));
    if (!repeatedCards.length) {
      return;
    }

    setRepeatMode('not_remembered');
    setActiveCards(repeatedCards);
    repeatRememberedCardsRef.current.clear();
    repeatSkipedCardsRef.current.clear();
    flippedOutCardsRef.current.clear();
    appliedInitialIndexRef.current = 0;
    updateVisibleCards(repeatedCards);
  };

  const handleResetProgress = () => {
    flippedOutCardsRef.current.clear();
    rememberedCardsRef.current.clear();
    skipedCardsRef.current.clear();
    repeatRememberedCardsRef.current.clear();
    repeatSkipedCardsRef.current.clear();
    setRepeatMode(null);
    setActiveCards(cards);

    appliedInitialIndexRef.current = 0;
    updateVisibleCards(cards);

    emitProgress();
    if (typeof onReset === 'function') {
      onReset();
    }
  };

  const handleLoadMoreCards = () => {
    if (wordsLoading) {
      return;
    }

    dispatchLoadMore();
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (wordsLoading || !visibleCards.stack.length) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, [contenteditable="true"]')) {
        return;
      }

      switch (event.key) {
        case ' ':
        case 'Space':
        case 'Spacebar':
          event.preventDefault();
          setTextMode((prev) => !prev);
          break;
        case 'Enter':
          event.preventDefault();
          handleTap(visibleCards.currentIndex);
          break;
        case 'ArrowLeft':
          castCardAway(false);
          break;
        case 'ArrowRight':
          castCardAway(true);
          break;
        case 'ArrowUp':
        case 'ArrowDown':
          if (flippedOutCardsRef.current.size) {
            restorePreviousCard();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [castCardAway, restorePreviousCard, visibleCards, wordsLoading]);

  const progressRememberedCount = isRepeatSession
    ? repeatRememberedCardsRef.current.size
    : rememberedCardsRef.current.size;
  const progressSkippedCount = isRepeatSession
    ? repeatSkipedCardsRef.current.size
    : skipedCardsRef.current.size;
  const progressAnsweredCount = progressRememberedCount + progressSkippedCount;
  const progressTotal = activeCards.length || 1;
  const completedWordsCount = progressRememberedCount + progressSkippedCount;
  const hasCompletedBatch = completedWordsCount > 0 && !visibleCards.stack.length;
  const endSummaryTitle = (() => {
    if (!hasCompletedBatch) {
      return '';
    }
    if (isRepeatSession) {
      if (mode === 'remembered' || repeatMode === 'remembered') {
        return 'You reviewed remembered cards in this set.';
      }
      if (mode === 'unstudied' || repeatMode === 'not_remembered') {
        return 'You reviewed unstudied cards in this set.';
      }
      return 'You completed this review set.';
    }
    if (progressSkippedCount > 0) {
      return 'You reviewed all words in this batch.';
    }
    return 'Great job, all words in this batch are learned.';
  })();
  const endSummaryText = (() => {
    if (!hasCompletedBatch) {
      return '';
    }
    if (isRepeatSession) {
      if (progressSkippedCount > 0) {
        return `${progressSkippedCount} ${progressSkippedCount === 1 ? 'card is' : 'cards are'} still not remembered in this set.`;
      }
      return 'You can close this set and continue with the main deck progress.';
    }
    if (progressSkippedCount > 0) {
      return `${progressSkippedCount} ${progressSkippedCount === 1 ? 'word is' : 'words are'} still not remembered. Repeat unstudied to lock them in.`;
    }
    if (loadMoreEnabled) {
      return 'You can watch now or load more words to prepare even better.';
    }
    return 'You can watch now, or reset progress for another round.';
  })();

  useEffect(() => {
    if (isRepeatSession || mode !== 'main') {
      return;
    }
    if (visibleCards.stack.length > 0) {
      return;
    }

    const danglingIds = Array.from(flippedOutCardsRef.current).filter((cardId) => {
      return !rememberedCardsRef.current.has(cardId) && !skipedCardsRef.current.has(cardId);
    });
    if (!danglingIds.length) {
      return;
    }

    danglingIds.forEach((cardId) => {
      skipedCardsRef.current.add(cardId);
      const cardData = cards.find((card) => card.id === cardId);
      if (cardData) {
        emitStatusChange(cardData, 'not_remembered');
      }
    });
    emitProgress();
  }, [cards, isRepeatSession, mode, visibleCards.stack.length]);

  return (
    <div className={`swipestack-cards__wrapper ${textMode ? 'swipestack-cards__text-mode' : ''}`}>
      <div className="swipestack-cards__top-panel">
        <FormCheck className="text-mode"
                   type="switch"
                   onChange={(event) => handleCardModeChange(event.target.checked)}
                   checked={textMode}
                   disabled={!visibleCards.stack.length}
        />
        <div className="progress-stacked">
          <div className="progress" style={{width: `${Math.round(progressRememberedCount * 100 / progressTotal)}%`}}>
            <div className="progress-bar remember-progress"/>
          </div>
          <div className="progress" style={{width: `${Math.round(progressSkippedCount * 100 / progressTotal)}%`}}>
            <div className="progress-bar skipped-progress"/>
          </div>
          <div className="progress-info"><span>{progressAnsweredCount}</span><span>{activeCards.length}</span></div>
        </div>
        <CloseButton onClick={handleCloseCards} />
      </div>
      <div className="swipestack-cards__deck">
        {!!visibleCards.stack.length && visibleCards.stack.map((card, _i) => {
            const i = _i - visibleCards.cutFactor;
            if (i < 0) {
              return null;
            }
            const isActive = card.id === visibleCards.currentId;
            return (
              <animated.div
                key={card.id}
                className={`swipestack-cards ${
                  isActive
                    ? 'swipestack-cards__active-card-wrapper'
                    : ''
                } swipestack-cards__card-wrapper swipestack-card-${i}`}
                style={{
                  '--swipestack-cards-index': i + 1,
                  '--swipestack-cards-stack-length': visibleCards.cutLength,
                  x: animationProps[_i]?.x,
                  y: animationProps[_i]?.y,
                  opacity: fadeEffect ? animationProps[_i]?.opacity : 1,
                } as CSSProperties}
              >
                {/* This is the card itself, we're binding our gesture to it (and inject its index so we know which is which) */}
                <animated.div
                  {...(visibleCards.currentIndex === _i ? bindGesture(_i) : {})}
                  className={`${
                    isActive
                      ? 'swipestack-cards__active-card'
                      : ''
                  } swipestack-cards__card`}
                  style={{
                    touchAction: 'none',
                    transform: interpolate(
                      [animationProps[_i]?.rot, animationProps[_i]?.scale],
                      // @ts-ignore
                      setTransforms
                    ),
                  }}
                >
                  <Card data={card} active={isActive} onMount={isActive ? handleActiveCardMount : undefined} onSave={onSave} />
                </animated.div>
              </animated.div>
            );
          })}
        {!visibleCards.stack.length && !wordsLoading && (
          <div className="swipestack-cards__end-actions d-flex flex-column text-center">
            {!!endSummaryTitle && (
              <div className="swipestack-cards__end-summary">
                <div className="swipestack-cards__end-summary-title">{endSummaryTitle}</div>
                <div className="swipestack-cards__end-summary-text">{endSummaryText}</div>
              </div>
            )}
            <div>
              {!!rememberedCardsRef.current.size && (<Button variant="secondary" onClick={handleRepeatRememberedCards}>Repeat remembered</Button>)}
              {!!skipedCardsRef.current.size && (<Button variant="dark" onClick={handleRepeatUnstudiedCards}>Repeat unstudied</Button>)}
            </div>
            <div>
              <Button variant="danger" onClick={handleResetProgress}>Reset progress</Button>
              {loadMoreEnabled && (<Button variant="primary" onClick={handleLoadMoreCards}>Load more</Button>)}
            </div>
          </div>
        )}
        {wordsLoading && (<div className="swipestack-cards__loading"><Spinner animation="border" /></div>)}
      </div>
      {!wordsLoading && !!visibleCards.stack.length && (
        <div className="swipestack-cards__bottom-panel">
          <div className="container">
            <ButtonToolbar className="justify-content-between">
              <Button variant="light" onClick={() => castCardAway(false)}><i className="ic ic-arrow-left"/> <span>Don't remember</span></Button>
              <Button className="btn-center" variant="dark" title="Previous card" onClick={restorePreviousCard} disabled={!flippedOutCardsRef.current.size}><i className="ic ic-restore"/></Button>
              <Button variant="light" onClick={() => castCardAway(true)}><span>I remember</span> <i className="ic ic-arrow-right"/></Button>
            </ButtonToolbar>
          </div>
        </div>
      )}
    </div>
  );
}

export default Deck;
